import { useForm } from 'react-hook-form';
import { Field, INPUT, INPUT_ERR } from './form-utils';
import type { VehicleType } from 'types/api.generated';

const VEHICLE_TYPES: { value: VehicleType; label: string }[] = [
  { value: 'bike', label: 'Bike' },
  { value: 'tricycle', label: 'Tricycle' },
  { value: 'car', label: 'Car' },
  { value: 'van', label: 'Van' },
  { value: 'truck', label: 'Truck' },
];

export interface PackageData {
  packageDescription: string;
  packageWeight?: number;
  vehicleTypeOverride?: VehicleType;
}

interface Props {
  defaultValues?: Partial<PackageData>;
  onNext: (data: PackageData) => void;
  onBack: () => void;
}

export default function StepPackage({ defaultValues, onNext, onBack }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PackageData>({ defaultValues });

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="fade-up bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span
              className="material-symbols-outlined text-primary text-[20px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              package_2
            </span>
          </div>
          <h3 className="font-black text-base text-slate-800">Package Details</h3>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="Package description" error={errors.packageDescription?.message}>
            <textarea
              {...register('packageDescription', { required: 'Required' })}
              rows={3}
              placeholder="e.g. Documents, shoes (size 42), birthday cake..."
              className={`${INPUT} resize-none ${errors.packageDescription ? INPUT_ERR : ''}`}
            />
          </Field>

          <Field
            label="Estimated weight (kg)"
            error={errors.packageWeight?.message}
            hint="Optional — affects final price (₦100 per kg)"
          >
            <input
              {...register('packageWeight', {
                min: { value: 0, message: 'Cannot be negative' },
                validate: (v) => !v || !isNaN(Number(v)) || 'Must be a number',
              })}
              type="number"
              min="0"
              step="0.1"
              placeholder="e.g. 1.5"
              className={`${INPUT} max-w-[180px] ${errors.packageWeight ? INPUT_ERR : ''}`}
            />
          </Field>

          <Field label="Vehicle size (optional)" hint="Override automatic tier from weight">
            <select {...register('vehicleTypeOverride')} className={`${INPUT} max-w-[220px]`} defaultValue="">
              <option value="">Auto from weight</option>
              {VEHICLE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-4 rounded-2xl border-2 border-slate-200 text-slate-600 font-extrabold text-sm hover:border-primary hover:text-primary transition-all"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          Back
        </button>
        <button
          type="submit"
          className="flex-1 bg-primary text-white font-extrabold px-8 py-4 rounded-2xl text-base hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.02] transition-all active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group"
        >
          <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
          <span className="relative z-10">Continue to Payment</span>
          <span className="material-symbols-outlined relative z-10 text-[20px]">arrow_forward</span>
        </button>
      </div>
    </form>
  );
}
